import { useState, useEffect, useCallback, useMemo } from "react";
import "../Css/Order.css";
import "../Css/OrderExcel.css";
import "../Css/Account.css";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import Navbar from "./Navbar";
import DatePicker from "react-datepicker";
import { Typography, CircularProgress } from "@mui/material";
import "react-datepicker/dist/react-datepicker.css";
import { MdDownloadForOffline } from "react-icons/md";
import Logger from "./Logger";
import jsPDF from "jspdf";
import "jspdf-autotable";
import 'font-awesome/css/font-awesome.min.css';
import { FaListUl } from "react-icons/fa";
import { getLoggerBaseURL } from '../utils/getLoggerBaseURL';

const Accounts = () => {
  const userEmail = sessionStorage.getItem("userEmail");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDriver, setSelectedDriver] = useState("");

  // Initialize Logger
  const logger = useMemo(() => new Logger(getLoggerBaseURL()), []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/driver/excel/${selectedDate.toISOString()}/${userEmail}?driver=${selectedDriver}`);

      if (response.ok) {
        const responseData = await response.json();
        if (!responseData || responseData.length === 0) {
          logger.log("No data available for the selected date", "INFO");
          setData([]);
          setLoading(false);
          return;
        }
        setData(responseData);
        setLoading(false);
      } else {
        logger.log("Failed to fetch data for Order Excel file", "ERROR");
        setError("Failed to fetch data");
        setLoading(false);
      }
    } catch (error) {
      logger.log(`Error fetching data: ${error.message}`, "ERROR");
      setError("Error fetching data");
      setLoading(false);
    }
  }, [selectedDate, selectedDriver, userEmail, logger]);

  const handleDownloadPdf = () => {
    // Initialize jsPDF in landscape mode
    const doc = new jsPDF("landscape");

    const date = new Date(selectedDate);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString().slice(-2);

    const pageWidth = doc.internal.pageSize.getWidth();
    const topMargin = 30;
    const leftMargin = 20;

    // Set Title with reduced font size
    const title = "Zordigo";
    const fontSize = 14; // Reduced font size
    const titleWidth = doc.getTextWidth(title);
    doc.setFontSize(fontSize);
    doc.text(title, (pageWidth - titleWidth) / 2, 20);
    doc.text("Accounts Report", (pageWidth - titleWidth - 11) / 2, 30);

    // Add Date and Accounts Report with reduced font size
    doc.setFontSize(10); // Reduced font size for text
    doc.text(`Date: ${day}-${month}-${year}`, leftMargin, topMargin + 5);

    // Add Driver Details with reduced font size
    const selectedDriverDetails = drivers.find(
      (driver) => driver._id === selectedDriver
    );

    if (selectedDriverDetails) {
      doc.setFontSize(10); // Reduced font size for driver details
      doc.text(
        `Driver Name: ${selectedDriverDetails.name}`,
        leftMargin,
        topMargin + 10
      );
      doc.text(
        `Driver Email: ${selectedDriverDetails.email}`,
        leftMargin,
        topMargin + 15
      );
      doc.text(
        `Driver Phone: ${selectedDriverDetails.phone}`,
        180,
        topMargin + 10
      );
      doc.text(
        `Vehicle: ${selectedDriverDetails.vehicle}`,
        180,
        topMargin + 15
      );
    }

    const sortedDataPdf = [...data].sort((a, b) => {
      // Compare using localeCompare for proper alphanumeric sorting
      return a.Order_No.localeCompare(b.Order_No, undefined, { numeric: true });
    });

    // Prepare Table Data
    const tableColumn = [
      "Order No.",
      "Company Name",
      "Cash",
      "UPI",
      "Cheque",
      "Signed Bill",
      "Bill with Party",
      "Bill Salesman",
      "Stock Return",
      "Round Off",
      "Failed Reason",
    ];
    const tableRows = [];

    let totalCashAmount = 0,
      totalUpiAmount = 0,
      totalChequeAmount = 0,
      totalSignedBill = 0,
      totalBillParty = 0,
      totalBillSalesman = 0,
      totalStock = 0,
      totalRound = 0;

    sortedDataPdf.forEach((item) => {
      let cashAmount = 0,
        upiAmount = 0,
        chequeAmount = 0,
        signedBill = 0,
        billParty = 0,
        billSalesman = 0,
        stock = 0,
        round = 0;

      if (item.mop === "Cash") cashAmount += item.amount1;
      else if (item.mop === "UPI" || item.mop === "Online") upiAmount += item.amount1;
      else if (item.mop === "Cheque") chequeAmount += item.amount1;
      else if (item.mop === "Signed Bill Return") signedBill += item.amount1;
      else if (item.mop === "Bill with Party") billParty += item.amount1;
      else if (item.mop === "Bill left with Sales Man") billSalesman += item.amount1;
      else if (item.mop === "Stock Return Value") stock += item.amount1;
      else if (item.mop === "Round Off") round += item.amount1;

      if (item.mop2 === "Cash") cashAmount += item.amount2;
      else if (item.mop2 === "UPI" || item.mop === "Online") upiAmount += item.amount2;
      else if (item.mop2 === "Cheque") chequeAmount += item.amount2;
      else if (item.mop2 === "Signed Bill Return") signedBill += item.amount2;
      else if (item.mop2 === "Bill with Party") billParty += item.amount2;
      else if (item.mop2 === "Bill left with Sales Man") billSalesman += item.amount2;
      else if (item.mop2 === "Stock Return Value") stock += item.amount2;
      else if (item.mop2 === "Round Off") round += item.amount2;

      if (item.mop3 === "Cash") cashAmount += item.amount3;
      else if (item.mop3 === "UPI" || item.mop === "Online") upiAmount += item.amount3;
      else if (item.mop3 === "Cheque") chequeAmount += item.amount3;
      else if (item.mop3 === "Signed Bill Return") signedBill += item.amount3;
      else if (item.mop3 === "Bill with Party") billParty += item.amount3;
      else if (item.mop3 === "Bill left with Sales Man") billSalesman += item.amount3;
      else if (item.mop3 === "Stock Return Value") stock += item.amount3;
      else if (item.mop3 === "Round Off") round += item.amount3;

      totalCashAmount += cashAmount;
      totalUpiAmount += upiAmount;
      totalChequeAmount += chequeAmount;
      totalSignedBill += signedBill;
      totalBillParty += billParty;
      totalBillSalesman += billSalesman;
      totalStock += stock;
      totalRound += round;

      const failedReason = item.o_status === "3" ? item.reason_failure : "-";

      tableRows.push([
        item.Order_No,
        item.Rec_Name,
        cashAmount > 0 ? cashAmount : "-",
        upiAmount > 0 ? upiAmount : "-",
        chequeAmount > 0 ? chequeAmount : "-",
        signedBill > 0 ? signedBill : "-",
        billParty > 0 ? billParty : "-",
        billSalesman > 0 ? billSalesman : "-",
        stock > 0 ? stock : "-",
        round > 0 ? round : "-",
        failedReason,
      ]);
    });

    // Add the Total Row
    tableRows.push([
      "Total",
      "-",
      totalCashAmount > 0 ? totalCashAmount : "-",
      totalUpiAmount > 0 ? totalUpiAmount : "-",
      totalChequeAmount > 0 ? totalChequeAmount : "-",
      totalSignedBill > 0 ? totalSignedBill : "-",
      totalBillParty > 0 ? totalBillParty : "-",
      totalBillSalesman > 0 ? totalBillSalesman : "-",
      totalStock > 0 ? totalStock : "-",
      totalRound > 0 ? totalRound : "-",
      "-", // No total for failed reasons
    ]);

    // Generate the Table
    const startY = topMargin + 22; // Adjust starting Y position for the table
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: startY, // Start below the driver details
      headStyles: { fillColor: "#07a6ef" },
      didDrawPage: (data) => {
        const { pageNumber } = data;
        const footerText = `Page ${pageNumber} | Date: ${new Date().toLocaleDateString()}`;
        doc.setFontSize(8); // Reduced footer font size
        doc.text(footerText, 14, doc.internal.pageSize.height - 10);
      },
      styles: {
        cellPadding: 1, // Reduced cell padding
        fontSize: 8, // Reduced table font size
      },
      rowHeight: 9, // Reduced row height
    });

    // Save the PDF
    doc.save(`Accounts-${day}-${month}-${year}.pdf`);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleDriverChange = async (e) => {
    const driverId = e.target.value;
    await setSelectedDriver(driverId);
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/driver/excel/${selectedDate.toISOString()}/${userEmail}?driver=${driverId}`);

      if (response.ok) {
        const responseData = await response.json();
        if (!responseData || responseData.length === 0) {
          logger.log("No data available for the selected date", "INFO");
          setData([]);
          setLoading(false);
          return;
        }
        setData(responseData);
        setLoading(false);
      } else {
        logger.log("Failed to fetch data for Order Excel file", "ERROR");
        setError("Failed to fetch data");
        setLoading(false);
      }
    } catch (error) {
      logger.log(`Error fetching data: ${error.message}`, "ERROR");
      setError("Error fetching data");
      setLoading(false);
    }
  };
  const fetchDrivers = useCallback(async () => {
    try {
      const response = await fetch(`/api/drivera/${userEmail}`);

      if (response.ok) {
        const data = await response.json();

        // Set the drivers state
        setDrivers(data);
      }
    } catch (error) {
      logger.log(`Error fetching drivers" ${error}`, "ERROR");
    }
  }, [userEmail, logger]);

  const filteredData = data.filter((item) =>
    item.Order_No?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  let totalCashAmount = 0;
  let totalUpiAmount = 0;
  let totalChequeAmount = 0;
  let totalSignedBill = 0;
  let totalBillParty = 0;
  let totalBillSalesman = 0;
  let totalStock = 0;
  let totalRound = 0;

  const sortedData = [...filteredData].sort((a, b) => {
    return a.Order_No.localeCompare(b.Order_No, undefined, { numeric: true });
  });

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);


  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Then use sortedData instead of filteredData in your rendering
  return (
    <>
      <div className="New accounts-page">
        <Navbar />
        <div className="accountToolbar">
          <h2 className="headS">
            <Link to="/Reports" className="reports">Reports</Link>
            <span className="crumbSep">{">"}</span>
            <span className="reporter">Accounts</span>
          </h2>
          <div className="accountFilters">
            <label htmlFor="driverSelect">Select Driver:</label>
            <select id="driverSelect" value={selectedDriver} onChange={handleDriverChange} className="accountPageSearchbar">
              <option value="">Select a Driver</option>
              {drivers.map((driver) => (
                <option key={driver._id} value={driver._id}>
                  {driver.name}
                </option>
              ))}
            </select>
            <input type="text" className="accountPageSearchbar" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} autoComplete="off" />
            <DatePicker selected={selectedDate} onChange={handleDateChange} dateFormat="yyyy-MM-dd" className="accountPageDatePicker" />
            <div className="accountPageUploadButton">
              <button className="choose-file-buttonn" onClick={handleDownloadPdf} style={{ background: "rgb(121, 198, 233)", border: "none", cursor: "pointer", padding: "6px" }} data-tooltip-id="download-tooltip">
                <MdDownloadForOffline size={30} />
                <Tooltip id="download-tooltip" place="bottom" content="Get PDF" />
              </button>
            </div>
          </div>
        </div>
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "80vh", }}>
            <CircularProgress />
            <Typography variant="h6" style={{ marginTop: "20px" }}>
              Fetching Data...
            </Typography>
          </div>
        ) : (
          <table className="orderTT">
            <thead>
              <tr>
                <th className="Oth">Order No.</th>
                <th className="Oth">Company Name</th>
                <th className="Oth">Cash</th>
                <th className="Oth">UPI</th>
                <th className="Oth">Cheque</th>
                <th className="Oth">Signed Bill Return</th>
                <th className="Oth">Bill with Party</th>
                <th className="Oth">Bill Left with Sales Man</th>
                <th className="Oth">Stock Return</th>
                <th className="Oth">Round-off</th>
                <th className="Oth">Reason for Failed Orders</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.length === 0 ? (
                <tr>
                  <td className="noOrders" colSpan="15">
                    <div className="center-content">
                      <div className="icon-wrapper">
                        <FaListUl className="icon" />
                      </div>
                      <span className="noOrdersMsg">No data available</span>
                    </div>
                  </td>
                </tr>
              ) : (
                sortedData.map((item, index) => {
                  // Initialize payment amounts for cash, upi, cheque
                  let cashAmount = 0;
                  let upiAmount = 0;
                  let chequeAmount = 0;
                  let signedBill = 0;
                  let billParty = 0;
                  let billSalesman = 0;
                  let stock = 0;
                  let round = 0;

                  // Check for mop and mop2 and mop3 and assign amounts to the respective columns
                  if (item.mop === "Cash") {
                    cashAmount += item.amount1;
                  } else if (item.mop === "UPI" || item.mop === "Online") {
                    upiAmount += item.amount1;
                  } else if (item.mop === "Cheque") {
                    chequeAmount += item.amount1;
                  } else if (item.mop === "Signed Bill Return") {
                    signedBill += item.amount1;
                  } else if (item.mop === "Bill with Party") {
                    billParty += item.amount1;
                  } else if (item.mop === "Bill left with Sales Man") {
                    billSalesman += item.amount1;
                  } else if (item.mop === "Stock Return Value") {
                    stock += item.amount1;
                  } else if (item.mop === "Round Off") {
                    round += item.amount1;
                  }

                  if (item.mop2 === "Cash") {
                    cashAmount += item.amount2;
                  } else if (item.mop2 === "UPI" || item.mop2 === "Online") {
                    upiAmount += item.amount2;
                  } else if (item.mop2 === "Cheque") {
                    chequeAmount += item.amount2;
                  } else if (item.mop2 === "Signed Bill Return") {
                    signedBill += item.amount2;
                  } else if (item.mop2 === "Bill with Party") {
                    billParty += item.amount2;
                  } else if (item.mop2 === "Bill left with Sales Man") {
                    billSalesman += item.amount2;
                  } else if (item.mop2 === "Stock Return Value") {
                    stock += item.amount2;
                  } else if (item.mop2 === "Round Off") {
                    round += item.amount2;
                  }

                  if (item.mop3 === "Cash") {
                    cashAmount += item.amount3;
                  } else if (item.mop3 === "UPI" || item.mop3 === "Online") {
                    upiAmount += item.amount3;
                  } else if (item.mop3 === "Cheque") {
                    chequeAmount += item.amount3;
                  } else if (item.mop3 === "Signed Bill Return") {
                    signedBill += item.amount3;
                  } else if (item.mop3 === "Bill with Party") {
                    billParty += item.amount3;
                  } else if (item.mop3 === "Bill left with Sales Man") {
                    billSalesman += item.amount3;
                  } else if (item.mop3 === "Stock Return Value") {
                    stock += item.amount3;
                  } else if (item.mop3 === "Round Off") {
                    round += item.amount3;
                  }

                  totalCashAmount += cashAmount;
                  totalUpiAmount += upiAmount;
                  totalChequeAmount += chequeAmount;
                  totalSignedBill += signedBill;
                  totalBillParty += billParty;
                  totalBillSalesman += billSalesman;
                  totalStock += stock;
                  totalRound += round;

                  return (
                    <tr key={item._id}>
                      <td className="Otd">{item.Order_No}</td>
                      <td className="Otdd">{item.Rec_Name}</td>
                      <td className="Otd">{cashAmount > 0 ? `₹ ${cashAmount}` : "-"}</td>
                      <td className="Otd">{upiAmount > 0 ? `₹ ${upiAmount}` : "-"}</td>
                      <td className="Otd">{chequeAmount > 0 ? `₹ ${chequeAmount}` : "-"}</td>
                      <td className="Otd">{signedBill > 0 ? `₹ ${signedBill}` : "-"}</td>
                      <td className="Otd">{billParty > 0 ? `₹ ${billParty}` : "-"}</td>
                      <td className="Otd">{billSalesman > 0 ? `₹ ${billSalesman}` : "-"}</td>
                      <td className="Otd">{stock > 0 ? `₹ ${stock}` : "-"}</td>
                      <td className="Otd">{round > 0 ? `₹ ${round}` : "-"}</td>
                      <td className="Otd">{item.o_status === "3" ? item.reason_failure : "-"}</td>
                    </tr>
                  );
                })
              )}
              <tr style={{ color: "#07a6ef" }}>
                <td className="Otd">
                  <strong>Total</strong>
                </td>
                <td className="Otdd">
                  <strong>-</strong>
                </td>
                <td className="Otd">
                  <strong>₹ {totalCashAmount}</strong>
                </td>
                <td className="Otd">
                  <strong>₹ {totalUpiAmount}</strong>
                </td>
                <td className="Otd">
                  <strong>₹ {totalChequeAmount}</strong>
                </td>
                <td className="Otd">
                  <strong>₹ {totalSignedBill}</strong>
                </td>
                <td className="Otd">
                  <strong>₹ {totalBillParty}</strong>
                </td>
                <td className="Otd">
                  <strong>₹ {totalBillSalesman}</strong>
                </td>
                <td className="Otd">
                  <strong>₹ {totalStock}</strong>
                </td>
                <td className="Otd">
                  <strong>₹ {totalRound}</strong>
                </td>
                <td className='Otd'>
                  <strong>-</strong>
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
      {error && (
        <Typography variant="h6" color="error" style={{ textAlign: "center", marginTop: "20px" }}>
          {error}
        </Typography>
      )}
    </>
  );
};

export default Accounts;
